import React, { useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  FileText,
  Layers3,
  Menu,
  Play,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import './VisualHomepage.css';
import VisualLogoMark from './VisualLogoMark';

const youtubeEmbedSrc = 'https://www.youtube-nocookie.com/embed/4k7VwizMcCc?rel=0&modestbranding=1';

const socialLinks = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/tangibl3net/' },
  { label: 'Instagram', href: 'https://www.instagram.com/tangibl3net/' },
  { label: 'YouTube', href: 'https://www.youtube.com/@Tangibl3Net' },
];

const LogoMark = VisualLogoMark;

const pillars = [
  ['Asset & Legal Structuring', 'Turn an asset or commercial right into an organized issuer file, coordinated with the appropriate legal and professional providers.', FileText],
  ['Digital Instruments & Tokenization', 'Configure controlled digital certificates, rights records, holder data, evidence, and transfer rules in one auditable workflow.', Layers3],
  ['Payments, Wallets & Settlement', 'Coordinate wallet, payment, and settlement workflows through appropriate specialist providers, where permitted.', BarChart3],
  ['Structured Finance & Institutional Services', 'Prepare eligible asset-backed and fiduciary programs for institutional review, subject to documentation, law, and provider arrangements.', Building2],
];

const journeySteps = [
  ['Assess', 'Review the asset, the right, and the file.'],
  ['Structure', 'Organize the issuer file with legal and professional providers.'],
  ['Digitize', 'Prepare controlled digital certificates and records.'],
  ['Settle', 'Coordinate payment, wallet, and settlement arrangements through specialist providers, where permitted.'],
  ['Administer', 'Maintain records, holders, and ongoing administration.'],
];

const pricingCards = [
  ['Issuer Desk', 'For individuals and small teams exploring tokenization.', 'USD 39–99', '/ user / month', Users],
  ['Issuer Workspace', 'For growing teams preparing issuer files and evidence.', 'USD 500', '/ month bundle', Layers3],
  ['Certificate Program', 'For structured programs and controlled certificates.', 'From USD 750', '/ month', FileText],
  ['Enterprise', 'For institutions with complex assets and custom requirements.', 'Custom', 'quoted', Building2],
];

const programs = [
  ['Tokenization Lab', 'Apply immediately, build the file, classify the asset, and prepare the program before activation.'],
  ['Fee-cover program', 'After 90 days live, eligible projects may apply for support covering up to 50% of approved Tangibl3Net fees.'],
  ['Verified Issuer Contest', 'A quarterly USD 10,000 program based on documentation, transparency, execution, and real-world asset utility.'],
];

const navItems = [
  ['Home', '#top'],
  ['How It Works', '#how'],
  ['Pillars', '#pillars'],
  ['Programs', '#programs'],
  ['Pricing', '#pricing'],
  ['Apply', '/apply'],
];

const VisualHomepage = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <main className="visual-homepage">
      <header className="visual-header">
        <a href="#top" className="visual-brand" aria-label="Tangibl3Net home">
          <LogoMark />
          <span>Tangibl3Net</span>
        </a>
        <nav className="visual-nav" aria-label="Main navigation">
          <a className="visual-nav__active" href="#top">Home</a>
          <a href="#how">How It Works</a>
          <a href="#pillars">Pillars</a>
          <a href="#programs">Programs</a>
          <a href="#pricing">Pricing</a>
          <a href="/apply">Apply</a>
        </nav>
        <div className="visual-header__actions">
          <a className="visual-apply" href="/apply">Apply now <ArrowRight size={17} /></a>
          <button
            className="visual-menu-button"
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
        {mobileOpen && (
          <nav className="visual-mobile-menu" aria-label="Mobile navigation">
            {navItems.map(([label, href]) => (
              <a key={label} href={href} onClick={() => setMobileOpen(false)}>{label}</a>
            ))}
          </nav>
        )}
      </header>

      <section id="top" className="visual-hero">
        <div className="visual-hero__copy">
          <p className="visual-kicker">Tangibl3Net — Institutional Infrastructure</p>
          <h1>Structure. Digitize. <span>Finance. Manage.</span></h1>
          <p className="visual-subtitle">
            Tangibl3 connects real-world assets and commercial rights with digital infrastructure through coordinated structuring, documentation, digital instruments, financing preparation, settlement arrangements and ongoing administration.
          </p>
          <div className="visual-hero__buttons">
            <a className="visual-button visual-button--primary" href="/apply">Start a file <ArrowRight size={18} /></a>
            <a className="visual-button visual-button--outline" href="#overview-video">Watch overview</a>
          </div>
          <div className="visual-chips">
            <div><ShieldCheck /> Asset-first</div>
            <div><Layers3 /> Institutional controls</div>
            <div><BarChart3 /> Integrated services</div>
          </div>
        </div>

        <div id="overview-video" className="visual-video-card">
          <iframe
            src={youtubeEmbedSrc}
            title="Tangibl3Net overview video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
          <div className="visual-video-card__overlay" aria-hidden="true">
            <div className="visual-video-card__tag">Tangibl3Net</div>
            <div className="visual-video-card__play"><Play fill="currentColor" /></div>
            <div className="visual-video-card__caption">Watch overview</div>
          </div>
          <div className="visual-video-card__copy">See Tangibl3Net in action</div>
        </div>
      </section>

      <section id="pillars" className="visual-section visual-pillars">
        <div className="visual-section__intro">
          <p className="visual-section__label">Four pillars</p>
          <h2>Four ways Tangibl3 moves a file forward.</h2>
          <p>Tangibl3 coordinates workflows and specialist providers. It does not present itself as independently operating an exchange, brokerage, custody, trustee, investment advisory, or other regulated service unless separately licensed or formally appointed.</p>
        </div>
        <div className="visual-pillars__grid">
          {pillars.map(([title, text, Icon]) => (
            <article key={title} className="visual-pillar-card">
              <Icon />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
        <p className="visual-scope-note">
          <strong>Finance</strong> means financing preparation and coordination — not lending, underwriting, or guaranteed financing.{' '}
          <strong>Accessibility</strong> means institutional usability and interoperability — not unrestricted retail investment access.
        </p>
      </section>

      <section id="how" className="visual-section visual-flow">
        <div className="visual-section__intro">
          <p className="visual-section__label">How it works</p>
          <h2>From assessment to administration, in five stages.</h2>
          <p>Assess → Structure → Digitize → Settle → Administer.</p>
        </div>
        <div className="visual-journey__steps">
          {journeySteps.map(([title, text], index) => (
            <article key={title} className="visual-step">
              <div className="visual-step__num">{index + 1}</div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="programs" className="visual-section visual-programs">
        <div className="visual-section__intro">
          <p className="visual-section__label">Programs</p>
          <h2>Apply now. Unlock special programs later.</h2>
          <p>Projects can enter the Lab immediately. Fee-cover and contest eligibility require 90 days live with a clean active file.</p>
        </div>
        <div className="visual-programs__grid">
          {programs.map(([title, text]) => (
            <article key={title} className="visual-program-card">
              <CheckCircle2 />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="pricing" className="visual-section visual-pricing">
        <div className="visual-section__intro">
          <p className="visual-section__label">Pricing</p>
          <h2>Plans for builders at every stage.</h2>
          <p>Clear monthly options to issue, manage, and scale real-world asset programs.</p>
        </div>
        <div className="visual-pricing__grid">
          {pricingCards.map(([name, text, price, suffix, Icon]) => (
            <article key={name} className={`visual-price-card ${name === 'Enterprise' ? 'visual-price-card--enterprise' : ''}`}>
              <Icon />
              <h3>{name}</h3>
              <p>{text}</p>
              <div className="visual-price-card__price">{price} <span>{suffix}</span></div>
              <a href={name === 'Enterprise' ? 'mailto:contact@tangibl3.net' : '/apply'}>{name === 'Enterprise' ? 'Contact sales' : 'Get started'}</a>
            </article>
          ))}
        </div>
      </section>

      <section id="scope" className="visual-section visual-scope">
        <div className="visual-section__intro">
          <p className="visual-section__label">What Tangibl3Net does</p>
          <h2>Available, partner-delivered, and planned — kept separate.</h2>
        </div>
        <div className="visual-scope__grid">
          <article className="visual-scope-card">
            <h3>Available now</h3>
            <p>Issuer file intake and coordination, documentation workflow, digital certificate preparation, records, and administration tooling.</p>
          </article>
          <article className="visual-scope-card">
            <h3>Partner-delivered</h3>
            <p>Legal structuring, trustee and fiduciary services, custody, payment and settlement services, and regulated services — provided by appropriately licensed, registered, or formally appointed providers.</p>
          </article>
          <article className="visual-scope-card">
            <h3>Planned infrastructure</h3>
            <p>Additional infrastructure capabilities, released only when ready. Planned items are not presented as available.</p>
          </article>
        </div>
      </section>

      <footer className="visual-footer">
        <div className="visual-footer__brand">
          <LogoMark compact />
          <strong>Tangibl3Net</strong>
          <span>Institutional infrastructure</span>
        </div>
        <p>
          Tangibl3Net supports controlled asset tokenization. It is not an exchange, broker, custodian, or investment adviser unless separately licensed or appointed. No guaranteed returns, liquidity, listings, or open trading.
        </p>
        <div className="visual-footer__social">
          {socialLinks.map(({ label, href }) => (
            <a key={label} href={href} target="_blank" rel="noreferrer">{label}</a>
          ))}
        </div>
      </footer>
    </main>
  );
};

export default VisualHomepage;
